import { useState } from 'react';
import type { Problem, Proposal, ProposalSummary } from '../api/types';
import { Badge } from '../components/Badge';
import { Button } from '../components/Button';
import { ProblemNotice } from '../components/ProblemNotice';
import { proposalStatusView } from '../app/status';
import { toProblem } from '../app/useAsync';

export interface ProposalBarProps {
  proposal: Proposal;
  /** AI のターンが queued / running。反映と見送りは API も 409 turn_in_progress にする（C7）。 */
  running: boolean;
  onShowDiff(): void;
  /** 失敗したら投げる。 */
  onAccept(): Promise<void>;
  onDiscard(): Promise<void>;
}

const CONFLICT_MESSAGE = '利用者の変更で下書きが進んでいます。見送って AI に取り込ませてください。';

/** 変更案の要約の 1 行。分類はファイル / 要素 / 出典（Claude Design の BPMN / Task / 契約とは違う。残課題）。 */
export function summaryText(summary: ProposalSummary | null): string {
  if (summary === null) return '変更案 · 要約を作成中';
  const { elements, sources } = summary;
  return `変更案 · ファイル ${summary.files} / 要素 ${elements.added + elements.removed + elements.changed} / 出典 ${sources.gained + sources.lost}`;
}

/** チャットの下端の変更案バー。反映と見送りに取り消しは無い（戻すときはリビジョン履歴から）。 */
export function ProposalBar({ proposal, running, onShowDiff, onAccept, onDiscard }: ProposalBarProps) {
  const [busy, setBusy] = useState(false);
  const [conflict, setConflict] = useState(false);
  const [problem, setProblem] = useState<Problem | null>(null);
  const status = proposalStatusView(proposal.status);

  const act = async (action: () => Promise<void>) => {
    setBusy(true);
    setConflict(false);
    setProblem(null);
    try {
      await action();
    } catch (error) {
      const failure = toProblem(error);
      // head が進んだときは状態を変えない（C7）。利用者の変更を AI に取り込ませる道を示す。
      if (failure.code === 'revision_conflict') setConflict(true);
      else setProblem(failure);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="studio-proposal-bar" aria-label="変更案">
      <div className="row">
        <Badge tone={status.tone}>{status.label}</Badge>
        <span className="studio-proposal-summary">{running ? '変更案を作成中' : summaryText(proposal.summary)}</span>
      </div>
      <div className="row">
        <Button variant="text" onClick={onShowDiff}>
          差分を見る
        </Button>
        <span className="spacer" />
        <Button disabled={running || busy} onClick={() => void act(onDiscard)}>
          見送る
        </Button>
        <Button variant="primary" disabled={running || busy} onClick={() => void act(onAccept)}>
          変更を反映
        </Button>
      </div>
      {conflict && (
        <div className="form-conflict" role="alert">
          {CONFLICT_MESSAGE}
        </div>
      )}
      <ProblemNotice problem={problem} tone={problem?.status === 409 ? 'notice' : 'error'} />
      <p className="studio-proposal-note">変更案あり · 反映するまで履歴は増えません</p>
    </section>
  );
}
